import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { parseCurrency, applyCurrencyMask } from '../utils/currency.ts';
import api from '../services/api.ts';
import FormPage from '../components/ui/FormPage.tsx';
import MoreOptions from '../components/ui/MoreOptions.tsx';

/** Nova dívida — FormPage (módulo separado de Contas). */
const CreateDebt: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const backPath = '/financeiro/dividas';

  const [creditorName, setCreditorName] = useState('');
  const [description, setDescription] = useState('');
  const [amountDisplay, setAmountDisplay] = useState('');
  const [amount, setAmount] = useState(0);
  const [remainingDisplay, setRemainingDisplay] = useState('');
  const [remaining, setRemaining] = useState(0);
  const [interestRate, setInterestRate] = useState(0);
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState<'loan' | 'financing' | 'consortium' | 'other'>('loan');
  const [installments, setInstallments] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation(
    async () => {
      await api.post('/financeiro/dividas', {
        creditorName,
        description,
        originalAmount: amount,
        currentAmount: remaining || amount,
        interestRate,
        dueDate,
        status: 'active',
        category,
        installments,
        currentInstallment: 1,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('debts');
        queryClient.invalidateQueries('financeiro-dividas');
        navigate(backPath);
      },
      onError: () => setErrors({ submit: 'Erro ao criar dívida. Tente novamente.' }),
    }
  );

  const validate = () => {
    const next: Record<string, string> = {};
    if (!description.trim()) next.description = 'Informe a descrição da dívida.';
    if (!creditorName.trim()) next.creditorName = 'Informe o credor.';
    if (!amount || amount <= 0) next.amount = 'Informe um valor maior que zero.';
    if (!dueDate) next.dueDate = 'Informe a data de vencimento.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) mutation.mutate();
  };

  return (
    <FormPage
      backTo={backPath}
      backLabel="Voltar às dívidas"
      breadcrumb="Financeiro / Dívidas / Nova"
      title="Nova dívida"
      subtitle="Empréstimo ou financiamento · módulo separado de Contas"
      hint="Campos essenciais primeiro. Use “Mais opções” só se precisar."
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(backPath)}>
            Cancelar
          </button>
          <button
            type="submit"
            form="create-debt-form"
            className="btn btn-primary"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar dívida'}
          </button>
        </>
      }
    >
      <form id="create-debt-form" noValidate onSubmit={onSubmit}>
        {errors.submit && <div className="field-error" style={{ marginBottom: 12 }}>{errors.submit}</div>}
        <div className="form-section-title">O essencial</div>
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="description">Descrição *</label>
            <input
              id="description"
              className={`input${errors.description ? ' error' : ''}`}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Ex: Financiamento imóvel, empréstimo banco"
            />
            {errors.description && <div className="field-error">{errors.description}</div>}
          </div>
          <div className="field">
            <label htmlFor="creditorName">Credor *</label>
            <input
              id="creditorName"
              className={`input${errors.creditorName ? ' error' : ''}`}
              value={creditorName}
              onChange={e => setCreditorName(e.target.value)}
            />
            {errors.creditorName && <div className="field-error">{errors.creditorName}</div>}
          </div>
          <div className="field">
            <label htmlFor="category">Tipo</label>
            <select
              id="category"
              className="select"
              value={category}
              onChange={e => setCategory(e.target.value as typeof category)}
            >
              <option value="loan">Empréstimo</option>
              <option value="financing">Financiamento</option>
              <option value="consortium">Consórcio/CDC</option>
              <option value="other">Outros</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="amount">Valor original *</label>
            <input
              id="amount"
              className={`input${errors.amount ? ' error' : ''}`}
              value={amountDisplay}
              onChange={e => {
                const masked = applyCurrencyMask(e.target.value);
                setAmountDisplay(masked);
                const v = parseCurrency(masked);
                setAmount(v);
                if (!remainingDisplay) {
                  setRemaining(v);
                }
              }}
              placeholder="R$ 0,00"
            />
            {errors.amount && <div className="field-error">{errors.amount}</div>}
          </div>
          <div className="field">
            <label htmlFor="dueDate">Vencimento *</label>
            <input
              id="dueDate"
              type="date"
              className={`input${errors.dueDate ? ' error' : ''}`}
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
            />
            {errors.dueDate && <div className="field-error">{errors.dueDate}</div>}
          </div>
        </div>
        <MoreOptions>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="remaining">Saldo atual</label>
              <input
                id="remaining"
                className="input"
                value={remainingDisplay}
                onChange={e => {
                  const masked = applyCurrencyMask(e.target.value);
                  setRemainingDisplay(masked);
                  setRemaining(parseCurrency(masked));
                }}
                placeholder="Igual ao original se vazio"
              />
            </div>
            <div className="field">
              <label htmlFor="interestRate">Juros (% a.m.)</label>
              <input
                id="interestRate"
                type="number"
                step="0.01"
                className="input"
                value={interestRate}
                onChange={e => setInterestRate(Number(e.target.value) || 0)}
              />
            </div>
            <div className="field">
              <label htmlFor="installments">Parcelas</label>
              <input
                id="installments"
                type="number"
                min={1}
                className="input"
                value={installments}
                onChange={e => setInstallments(Number(e.target.value) || 1)}
              />
            </div>
          </div>
        </MoreOptions>
      </form>
    </FormPage>
  );
};

export default CreateDebt;
